import React, { useState } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
export interface ReleaseDetails {
  disposition: string;
  unqualifiedReason?: string;
  creditScore?: string;
  note: string;
}
interface ReleaseLoanModalProps {
  isOpen: boolean;
  hasNextLoan: boolean;
  onClose: () => void;
  onEndSession: (details: ReleaseDetails) => void;
  onClaimNext: (details: ReleaseDetails) => void;
}
const DISPOSITIONS = ['Contacted – Will Proceed', 'Contacted – Undecided', 'Left Voicemail', 'No Answer', 'Requested Callback', 'Not Interested – Rate', 'Low Credit/Unqualified'];
const UNQUALIFIED_REASONS = ['Credit score below guidelines', 'DTI exceeds guidelines', 'Insufficient income documentation', 'Collateral / appraisal issue'];
const selectClass = 'h-11 w-full appearance-none rounded-xl border border-brand-200 bg-white px-3.5 pr-10 text-sm text-brand-500 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500';
export function ReleaseLoanModal({
  isOpen,
  hasNextLoan,
  onClose,
  onEndSession,
  onClaimNext
}: ReleaseLoanModalProps) {
  const [disposition, setDisposition] = useState('');
  const [unqualifiedReason, setUnqualifiedReason] = useState('');
  const [creditScore, setCreditScore] = useState('');
  const [note, setNote] = useState('');
  const requiresUnqualified = disposition === 'Low Credit/Unqualified';
  const isValid = disposition.length > 0 && note.trim().length > 0 && (!requiresUnqualified || unqualifiedReason.length > 0 && creditScore.trim().length > 0);
  const details: ReleaseDetails = {
    disposition,
    unqualifiedReason: requiresUnqualified ? unqualifiedReason : undefined,
    creditScore: requiresUnqualified ? creditScore : undefined,
    note: note.trim()
  };
  const reset = () => {
    setDisposition('');
    setUnqualifiedReason('');
    setCreditScore('');
    setNote('');
  };
  const handleClose = () => {
    reset();
    onClose();
  };
  return <Modal isOpen={isOpen} onClose={handleClose} maxWidth="xl" noPadding data-id="element-143" data-mp="1ffmnlgrob312">
      <div className="px-7 pb-6 pt-7" data-id="element-144" data-mp="1fhzbeak447m1">
        <span className="mb-2 block text-[11px] font-medium uppercase tracking-[0.14em] text-brand-300" data-id="element-145" data-mp="1aoo82jeie8yv">
          Release Loan
        </span>
        <h2 id="release-title" className="text-[22px] font-semibold tracking-[-0.02em] text-brand-500" data-id="element-146" data-mp="15qcvw20qrmdh">
          Save Release Details
        </h2>
        <p className="mt-2 text-[13px] leading-relaxed text-brand-400" data-id="element-147" data-mp="1ydfqwk67leyz">
          A disposition and release note are required before this loan returns
          to the queue. Low Credit/Unqualified also requires an unqualified
          reason and a credit score.
        </p>
      </div>

      <div className="space-y-5 border-y border-brand-200 px-7 py-6" data-id="element-148" data-mp="1kdxwnpa599i8">
        <div data-id="element-149" data-mp="d15y559430ta">
          <label htmlFor="release-disposition" className="mb-2 block text-[13px] font-semibold text-brand-500" data-id="element-150" data-mp="d1xkrqkqdo7f">
            Release Disposition{' '}
            <span className="font-normal text-brand-400" data-id="element-151" data-mp="2knftnpvygkt9">(Required)</span>
          </label>
          <div className="relative" data-id="element-152" data-mp="2oorb384odzqd">
            <select id="release-disposition" value={disposition} onChange={event => setDisposition(event.target.value)} className={selectClass} data-id="element-153" data-mp="2opsv423o5wk8">
              <option value="" data-id="element-154" data-mp="2yg5f9t7knnpu">Select a disposition…</option>
              {DISPOSITIONS.map(option => <option key={option} value={option} data-id="element-155" data-mp="33ddzdzca61tq">
                  {option}
                </option>)}
            </select>
            <span aria-hidden="true" className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-brand-300" data-id="element-156" data-mp="3celllj1ivc7g">
              ▾
            </span>
          </div>
        </div>

        {requiresUnqualified && <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_140px]" data-id="element-157" data-mp="1i2faslcjixfo">
            <div data-id="element-158" data-mp="1mzxkwf2anip9">
              <label htmlFor="unqualified-reason" className="mb-2 block text-[13px] font-semibold text-brand-500" data-id="element-159" data-mp="1n1i3d6bc3b6r">
                Unqualified Reason{' '}
                <span className="font-normal text-brand-400" data-id="element-160" data-mp="ms8mtzo5475c">(Required)</span>
              </label>
              <div className="relative" data-id="element-161" data-mp="11lc6cclakuyp">
                <select id="unqualified-reason" value={unqualifiedReason} onChange={event => setUnqualifiedReason(event.target.value)} className={selectClass} data-id="element-162" data-mp="wnsl2sq6fhx9">
                  <option value="" data-id="element-163" data-mp="ioas015ulii3">Select a reason…</option>
                  {UNQUALIFIED_REASONS.map(option => <option key={option} value={option} data-id="element-164" data-mp="dxkmmnxf9fne">
                      {option}
                    </option>)}
                </select>
                <span aria-hidden="true" className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-brand-300" data-id="element-165" data-mp="3hekm50d9chzj">
                  ▾
                </span>
              </div>
            </div>
            <div data-id="element-166" data-mp="21otelz6w3d00">
              <label htmlFor="credit-score" className="mb-2 block text-[13px] font-semibold text-brand-500" data-id="element-167" data-mp="21pbmxu6tthoa">
                Credit Score
              </label>
              <input id="credit-score" inputMode="numeric" value={creditScore} onChange={event => setCreditScore(event.target.value.replace(/\D/g, ''))} placeholder="000" className="h-11 w-full rounded-xl border border-brand-200 bg-white px-3.5 font-mono text-sm text-brand-500 placeholder:text-brand-300 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500" data-id="element-168" data-mp="1rv2ut7lpjqqn" />
            </div>
          </div>}

        <div data-id="element-169" data-mp="zab9plbxnwuk">
          <label htmlFor="release-note" className="mb-2 block text-[13px] font-semibold text-brand-500" data-id="element-170" data-mp="z6njav1sbqk2">
            Retention Release Note{' '}
            <span className="font-normal text-brand-400" data-id="element-171" data-mp="2wwubw7lyd7w1">(Required)</span>
          </label>
          <textarea id="release-note" value={note} onChange={event => setNote(event.target.value)} placeholder="Document the contact outcome and why this file is being released…" className="min-h-[120px] w-full resize-none rounded-xl border border-brand-200 bg-white p-3.5 text-sm text-brand-500 placeholder:text-brand-300 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500" data-id="element-172" data-mp="2s4awd53pg2ol" />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 bg-brand-50 px-7 py-5" data-id="element-173" data-mp="2497xs5f3mkux">
        <Button variant="ghost" onClick={handleClose} data-id="element-174" data-mp="7e3dh9a3kydf">
          Cancel
        </Button>
        <div className="flex flex-wrap items-center gap-3" data-id="element-175" data-mp="2hcykpa148tl">
          <Button variant="outline" disabled={!isValid} onClick={() => {
          onEndSession(details);
          reset();
        }} data-id="element-176" data-mp="axkdinldymw2">
            Save &amp; End Session
          </Button>
          {hasNextLoan && <Button disabled={!isValid} onClick={() => {
          onClaimNext(details);
          reset();
        }} data-id="element-177" data-mp="jryqukmcvncj">
              Save &amp; Claim Next
            </Button>}
        </div>
      </div>
    </Modal>;
}